import { useMemo, useState } from 'react';
import type { TimeSeriesPoint, TimeRange } from '@/mocks/superAdminAnalytics';

interface TimeSeriesChartProps {
  data: TimeSeriesPoint[];
  timeRange: TimeRange;
  title: string;
  barColor?: string;
  previousBarColor?: string;
}

function formatAxisValue(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
}

export default function TimeSeriesChart({
  data,
  timeRange,
  title,
  barColor = 'bg-amber-500',
  previousBarColor = 'bg-amber-200',
}: TimeSeriesChartProps) {
  const [tooltip, setTooltip] = useState<{ idx: number; x: number; y: number } | null>(null);

  const maxValue = useMemo(() => {
    if (data.length === 0) return 1;
    return Math.max(...data.map((d) => Math.max(d.value, d.previousValue)));
  }, [data]);

  const labels = useMemo(() => {
    if (timeRange === 'diario' && data.length > 24) {
      // Show every 3rd hour for daily
      return data.map((d, i) => (i % 3 === 0 ? d.label : ''));
    }
    if (timeRange === 'mensual' && data.length > 20) {
      // Show every 5th day for monthly
      return data.map((d, i) => (i % 5 === 0 ? d.label : ''));
    }
    if (timeRange === 'semanal') {
      return data.map((d) => d.label);
    }
    return data.map((d) => d.label);
  }, [data, timeRange]);

  if (data.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <p className="text-foreground-400 text-xs">Sin datos disponibles</p>
      </div>
    );
  }

  const barCount = data.length;
  const isDense = barCount > 15;

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-5 relative">
      <h4 className="text-foreground-800 text-xs font-semibold mb-4">{title}</h4>

      {/* Y-axis labels */}
      <div className="flex items-end gap-0.5" style={{ height: isDense ? 120 : 160 }}>
        {/* Y-axis ticks */}
        <div className="flex flex-col justify-between h-full pr-2 shrink-0" style={{ width: 36 }}>
          {[4, 3, 2, 1, 0].map((tick) => (
            <span key={tick} className="text-foreground-300 text-[8px] leading-none text-right">
              {formatAxisValue((maxValue * tick) / 4)}
            </span>
          ))}
        </div>

        {/* Grid lines and bars */}
        <div className="flex-1 relative h-full">
          {/* Horizontal grid lines */}
          {[0, 1, 2, 3].map((tick) => (
            <div
              key={tick}
              className="absolute left-0 right-0 border-t border-background-200"
              style={{ bottom: `${(tick / 4) * 100}%` }}
            ></div>
          ))}

          {/* Bars container */}
          <div className="relative flex items-end gap-px h-full">
            {data.map((point, idx) => {
              const currentHeight = (point.value / maxValue) * 100;
              const previousHeight = (point.previousValue / maxValue) * 100;
              const barWidth = isDense ? 'calc((100% - 3px) / ' + barCount + ')' : undefined;

              return (
                <div
                  key={idx}
                  className="relative flex-1 flex flex-col items-center justify-end h-full group cursor-pointer"
                  style={barWidth ? { width: barWidth, flex: 'none' } : undefined}
                  onMouseEnter={(e) => {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const parentRect = (e.currentTarget.parentElement?.parentElement as HTMLElement)?.getBoundingClientRect();
                    setTooltip({
                      idx,
                      x: rect.left - (parentRect?.left ?? 0) + rect.width / 2,
                      y: rect.top - (parentRect?.top ?? 0),
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                >
                  {/* Current period */}
                  <div
                    className={`w-full max-w-[16px] ${barColor} rounded-t-sm transition-all group-hover:brightness-110 relative`}
                    style={{ height: `${currentHeight}%`, minHeight: point.value > 0 ? 2 : 0 }}
                  >
                    {!isDense && currentHeight > 8 && (
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-foreground-300 text-[7px] font-mono opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {formatAxisValue(point.value)}
                      </span>
                    )}
                  </div>
                  {/* Previous period (behind) */}
                  <div
                    className={`absolute bottom-0 w-full max-w-[16px] ${previousBarColor} rounded-t-sm opacity-60`}
                    style={{ height: `${previousHeight}%`, minHeight: point.previousValue > 0 ? 2 : 0 }}
                  ></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex items-start gap-px mt-1.5 ml-[36px]">
        {labels.map((label, idx) => (
          <div
            key={idx}
            className={`flex-1 text-center ${label ? '' : 'opacity-0 pointer-events-none'}`}
            style={isDense ? { width: `calc((100% - 3px) / ${barCount})`, flex: 'none' } : undefined}
          >
            <span className="text-foreground-300 text-[7px] leading-tight">{label}</span>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip !== null && (
        <div
          className="absolute z-10 bg-foreground-950 text-background-50 rounded-lg px-2.5 py-1.5 text-[10px] font-medium pointer-events-none whitespace-nowrap shadow-lg"
          style={{
            left: Math.min(tooltip.x + 36, 300),
            top: Math.max(tooltip.y - 40, 0),
            transform: 'translate(-50%, -100%)',
          }}
        >
          <p className="font-bold text-[10px]">{data[tooltip.idx].label}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              {formatAxisValue(data[tooltip.idx].value)}
            </span>
            <span className="flex items-center gap-1 text-foreground-300">
              <span className="w-2 h-2 rounded-full bg-amber-200"></span>
              {formatAxisValue(data[tooltip.idx].previousValue)}
            </span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 ml-[36px]">
        <span className="flex items-center gap-1.5 text-foreground-400 text-[9px]">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-500"></span>
          Actual
        </span>
        <span className="flex items-center gap-1.5 text-foreground-300 text-[9px]">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-200"></span>
          Anterior
        </span>
      </div>
    </div>
  );
}